import React, { useEffect, useState } from "react";
import { Alert, Avatar, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AlternateEmailRoundedIcon from "@mui/icons-material/AlternateEmailRounded";
import { authRequest, refreshSession } from "../services/auth";
import { ErrorState, LoadingState } from "./AsyncViewState";
import { friendlyRole } from "../services/roles";

export default function ProfilePage({ activeRole, availableRoles, onRoleChange, onSessionRefresh }) {
  const [profile, setProfile] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    let mounted = true;
    authRequest().get("/profile")
      .then(({ data }) => { if (mounted) { setProfile(data); setEmail(data.email || ""); } })
      .catch((err) => { if (mounted) setError(err.response?.data?.error || "Unable to load your profile"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const saveEmail = async (event) => {
    event.preventDefault();
    setError(""); setSuccess(""); setSaving(true);
    if (!/^[^\s@]+@cognizant\.com$/i.test(email.trim())) {
      setError("Email must end with @cognizant.com"); setSaving(false); return;
    }
    try {
      const { data } = await authRequest().put("/profile/email", { email: email.trim() });
      setProfile((current) => ({ ...current, email: data.email, emailVerified: data.emailVerified }));
      setSuccess(data.message || "Email added to your profile");
      await refreshSession();
      onSessionRefresh?.();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update your email");
    } finally { setSaving(false); }
  };

  if (loading) return <LoadingState label="Loading your profile…" minHeight={320} />;
  if (error && !profile) return <ErrorState onRetry={() => window.location.reload()} message={error} />;

  const initials = (profile?.name || profile?.userId || "U").split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  return <Box sx={{ maxWidth: 760, mx: "auto", py: { xs: 1, md: 3 } }}>
    <Paper variant="outlined" sx={{ borderRadius: 4, overflow: "hidden" }}>
      <Box sx={{ p: { xs: 3, md: 4 }, color: "white", background: "linear-gradient(135deg, #075da8 0%, #1686b8 55%, #27a9b9 100%)" }}>
        <Stack direction="row" spacing={2.5} alignItems="center">
          <Avatar sx={{ width: 64, height: 64, bgcolor: "rgba(255,255,255,.18)", color: "white", fontWeight: 800, border: "1px solid rgba(255,255,255,.35)" }}>{initials || <AccountCircleOutlinedIcon />}</Avatar>
          <Box><Typography variant="h4" sx={{ fontWeight: 800 }}>{profile?.name}</Typography><Typography sx={{ opacity: .8 }}>Employee ID: {profile?.userId}</Typography></Box>
        </Stack>
      </Box>
      <Box sx={{ px: { xs: 3, md: 4 }, pt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 750, mb: 1 }}>Active role</Typography>
        {availableRoles.length > 1 ? <TextField select label="Active role" value={activeRole} onChange={(event) => onRoleChange(event.target.value)} size="small" sx={{ minWidth: 220 }}>
          {availableRoles.map((role) => <MenuItem key={role} value={role}>{friendlyRole(role)}</MenuItem>)}
        </TextField> : <Chip label={friendlyRole(activeRole)} color="primary" variant="outlined" />}
      </Box>
      <Box component="form" onSubmit={saveEmail} sx={{ p: { xs: 3, md: 4 } }}>
        <Typography variant="h6" sx={{ fontWeight: 750, mb: .5 }}>Contact email</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>Add your Cognizant email if your account does not have one yet.</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="flex-start">
          <TextField
            label="Email address"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={Boolean(profile?.email) || saving}
            required
            fullWidth
            helperText={profile?.email ? "Your email is already set. Contact a SuperAdmin if it needs to be replaced." : "Only @cognizant.com addresses are accepted."}
            InputProps={{ startAdornment: <AlternateEmailRoundedIcon color="action" sx={{ mr: 1 }} /> }}
          />
          {!profile?.email && <Button type="submit" variant="contained" disabled={saving} sx={{ minWidth: 145, minHeight: 56, textTransform: "none" }}>{saving ? <CircularProgress size={22} /> : "Save email"}</Button>}
        </Stack>
      </Box>
    </Paper>
  </Box>;
}
